import { Checkbox, Divider } from "antd"
import mapPinIcon from "assets/svg/MapPinArea.svg"
import alarmIcon from "assets/svg/alarm.svg"
import { Button as CustomButton } from "components"
import CustomToast from "./CustomToast"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useState } from "react"
import { CustomSpin } from "components/custom/Loader"

type ClockInBody = {
  longitude: number | null
  latitude: number | null
  method: "web-app"
  reason: string
  dutyLocationId?: number | null
}
type ClockOutBody = {
  longitude: number | null
  latitude: number | null
  overtimeSubmission: boolean
}

type SelectShiftPeriodContentProp = {
  type: string
  geofence: string | null
  geofenceError?: string | null
  isCheckingGeofence?: boolean | undefined
  status: string
  dutyLocationId?: number | null
  geofenceEnabled: boolean | undefined
  alreadyClockedOut: string | null
  currentTime: string
  duration: string
  latitude: number | null
  longitude: number | null
  isOvertimeAllowed: boolean
  shiftPlanId: number | undefined
  groupBy: "day" | "week" | "month"
  shiftPeriodId: number | undefined
  shiftEndTime: string | undefined
  mutateClockIn?: (body: ClockInBody) => void
  isLoadingClockIn?: boolean
  mutateClockout?: (body: ClockOutBody) => void
  isLoadingClockout?: boolean
  alreadyClockedIn?: boolean
}

export const SelectShiftPeriodContent = ({
  type,
  geofenceEnabled,
  shiftEndTime,
  latitude,
  longitude,
  alreadyClockedOut,
  dutyLocationId,
  alreadyClockedIn,
  geofence,
  geofenceError,
  isCheckingGeofence,
  status,
  currentTime,
  duration,
  isOvertimeAllowed,
  groupBy,
  shiftPlanId,
  shiftPeriodId,
  mutateClockIn,
  isLoadingClockIn,
  mutateClockout,
  isLoadingClockout,
}: SelectShiftPeriodContentProp) => {
  const [submitOvertime, setSubmitOvertime] = useState(false)
  const isAfterShift = status === "AfterShift"
  const queryClient = useQueryClient()

  const startDate = "2020-01-01"
  const endDate = new Date().toISOString().split("T")[0]

  const overtimeQueryKey = `/v1/time-attendance/employee/overtime?startDate=${startDate}&endDate=${endDate}&limit=1000&groupBy=${groupBy}`

  const handleClockIn = () => {
    if (!shiftPlanId || !shiftPeriodId || !mutateClockIn) return
    if (alreadyClockedIn) {
      CustomToast("error", "You've already clocked in for this shift.")
      return
    }

    const clockInBody: ClockInBody = {
      latitude,
      longitude,
      method: "web-app",
      reason: "",
    }
    if (dutyLocationId !== undefined) {
      clockInBody.dutyLocationId = dutyLocationId
    }
    mutateClockIn(clockInBody)
  }

  const handleClockout = () => {
    if (!shiftPlanId || !shiftPeriodId || !mutateClockout) return
    if (alreadyClockedOut) {
      CustomToast("error", "You've already clocked out for this shift.")
      return
    }

    mutateClockout({
      latitude,
      longitude,
      overtimeSubmission: submitOvertime && isOvertimeAllowed && !!shiftEndTime,
    })
  }

  const bracketText =
    type === "clock-in"
      ? status === "Late"
        ? `${duration} late`
        : `${duration} until shift starts`
      : isAfterShift
      ? `${duration} after shift's end`
      : `${duration} until shift ends`

  return (
    <>
      <Divider style={{ margin: "15px 0" }} />
      <div className="flex flex-col md:flex-row flex-wrap items-start md:items-center gap-2 mb-3">
        {geofenceEnabled && (
          <>
            <span className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1">
                  <img
                    src={mapPinIcon}
                    width={15}
                    height={15}
                  />
                  <p className="text-sm font-semibold">Within geofence:</p>
                </div>
                {isCheckingGeofence ? (
                  <CustomSpin spinning />
                ) : (
                  <strong
                    style={{
                      color:
                        geofence === "Yes"
                          ? "#00A63E"
                          : geofence === "No" || geofence === "Unavailable"
                          ? "#FB2C36"
                          : undefined,
                    }}
                  >
                    {geofence}
                  </strong>
                )}
              </div>
              {geofenceError && (
                <p className="max-w-[360px] text-xs leading-5 text-[#FB2C36]">
                  {geofenceError}
                </p>
              )}
            </span>

            <Divider
              type="vertical"
              className="bg-slate-300 hidden md:block"
            />
          </>
        )}

        <span className="flex items-center gap-1">
          <div className="flex items-center gap-1">
            <img
              src={alarmIcon}
              width={15}
              height={15}
            />
            <p className="text-sm font-semibold">
              {type === "clock-in" ? "Arrival status:" : "Departure status:"}
            </p>
          </div>
          <strong
            className={`${
              isAfterShift
                ? "text-[#00A63E]"
                : status.toLowerCase() === "early" && type === "clock-in"
                ? "text-[#00A63E]"
                : status.toLowerCase() === "late" && type === "clock-in"
                ? "text-[#FB2C36]"
                : status.toLowerCase() === "early" && type === "clock-out"
                ? "text-[#FB2C36]"
                : "text-[#00A63E]"
            }`}
          >
            {isAfterShift ? "Ok" : status}
          </strong>
        </span>
        <Divider
          type="vertical"
          className="bg-slate-300 hidden md:block"
        />
        <span className="flex items-center gap-3">
          <div className="flex items-center gap-1 font-semibold">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6 h-4 w-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            Current time: {currentTime}
          </div>
          {shiftPeriodId && (
            <span>
              [<span className="text-[#D08700]">{bracketText}</span>]
            </span>
          )}
        </span>
        {type === "clock-out" && isAfterShift && isOvertimeAllowed && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={submitOvertime}
              onChange={(e) => {
                setSubmitOvertime(e.target.checked)
              }}
            >
              Submit overtime
            </Checkbox>
          </div>
        )}
      </div>

      <CustomButton
        onClick={() => {
          type === "clock-in" ? handleClockIn() : handleClockout()
        }}
        color="primary"
        submitting={type === "clock-in" ? isLoadingClockIn : isLoadingClockout}
        disabled={
          !shiftPeriodId ||
          (type === "clock-out" && !alreadyClockedIn) ||
          (type === "clock-in" ? isLoadingClockIn : isLoadingClockout)
        }
      >
        {type === "clock-in" ? "Clock in" : "Clock out"}
      </CustomButton>
    </>
  )
}
